import scrollama from 'scrollama';
import * as d3 from 'd3';

// ========================================
// VIDEO AUTOPLAY HANDLING
// ========================================

const muteAndPlayVideo = (video) => {
  video.muted = true;
  video.defaultMuted = true;
  video.volume = 0;

  if (!video.hasAttribute('muted')) video.setAttribute('muted', '');
  if (!video.hasAttribute('playsinline')) video.setAttribute('playsinline', '');
  if (!video.hasAttribute('autoplay')) video.setAttribute('autoplay', '');
  if (!video.hasAttribute('loop')) video.setAttribute('loop', '');

  if (video.paused) {
    video.play().catch((error) => {
      console.log('Video autoplay prevented:', error);
    });
  }
};

const findAndMuteVideos = () => {
  const videos = document.querySelectorAll(
    '.photo-article video, .photo-article__hero-media video, .story-section__media video'
  );

  for (const video of videos) {
    muteAndPlayVideo(video);
  }

  // Handle Panopto iframes
  const panoptoIframes = document.querySelectorAll(
    ".photo-article iframe[src*='panopto'], .photo-article__hero-media iframe[src*='panopto'], .story-section__media iframe[src*='panopto']"
  );

  for (const iframe of panoptoIframes) {
    const currentSrc = iframe.src;
    const params = [];

    if (!currentSrc.includes('autoplay=true')) params.push('autoplay=true');
    if (!currentSrc.includes('muted=true')) params.push('muted=true');
    if (!currentSrc.includes('loop=true')) params.push('loop=true');
    if (!currentSrc.includes('hideControls=true')) params.push('hideControls=true');

    if (params.length > 0) {
      const separator = currentSrc.includes('?') ? '&' : '?';
      iframe.src = `${currentSrc}${separator}${params.join('&')}`;
    }
  }
};

// ========================================
// SCROLLAMA SETUP (NV-style with d3)
// ========================================

var stickyScrolly = d3.select('.photo-article__stories');
var stickyFigure = d3.selectAll('.story-section__media');
var stickyStep = d3.selectAll('.story-section__content .field__item.story-step');
var stickyBackgrounds = [...document.querySelectorAll('.story-section__media')];
var stickyScroller = scrollama();

// Track current step index (-1 means we're on hero, not in stories yet)
var currentStepIndex = -1;
var totalSteps = 0;
var scrollArrowNext = null;
var scrollArrowBack = null;
var hasEnteredStories = false;

function handleResize() {
  var stepH = Math.floor(window.innerHeight * 0.75);
  stickyStep.style('min-height', stepH + 'px');

  var figureHeight = window.innerHeight;
  stickyFigure
    .style('height', figureHeight + 'px');

  stickyScroller.resize();
}

// Create scroll navigation arrows (back and next)
function createScrollArrows() {
  // Append to .photo-article so it's visible on hero too
  const photoArticle = document.querySelector('.photo-article');
  if (!photoArticle || document.querySelector('.story-scroll-nav')) return;

  // Create container for both arrows
  const navContainer = document.createElement('div');
  navContainer.className = 'story-scroll-nav';

  // Back arrow
  scrollArrowBack = document.createElement('button');
  scrollArrowBack.className = 'story-scroll-arrow story-scroll-arrow--back';
  scrollArrowBack.setAttribute('aria-label', 'Scroll to previous section');
  scrollArrowBack.innerHTML = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  // Next arrow
  scrollArrowNext = document.createElement('button');
  scrollArrowNext.className = 'story-scroll-arrow story-scroll-arrow--next';
  scrollArrowNext.setAttribute('aria-label', 'Scroll to next section');
  scrollArrowNext.innerHTML = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 5V19M12 19L5 12M12 19L19 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  navContainer.appendChild(scrollArrowBack);
  navContainer.appendChild(scrollArrowNext);
  photoArticle.appendChild(navContainer);

  scrollArrowBack.addEventListener('click', scrollToPrevStep);
  scrollArrowNext.addEventListener('click', scrollToNextStep);

  // Initially hide back arrow (we're on hero)
  updateScrollArrows();
}

// Scroll to the previous step
function scrollToPrevStep() {
  const steps = document.querySelectorAll('.story-section__content .field__item.story-step');
  const photoArticle = document.querySelector('.photo-article');
  const hero = document.querySelector('.photo-article__hero');
  
  if (!photoArticle) return;

  // If we're on step 0 or haven't entered stories, go to hero
  if (!hasEnteredStories || currentStepIndex <= 0) {
    if (hero) {
      hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
      hasEnteredStories = false;
      currentStepIndex = -1;
      updateScrollArrows();
    }
    return;
  }

  // Go to previous step
  let targetIndex = currentStepIndex - 1;
  
  if (targetIndex >= 0 && targetIndex < steps.length) {
    const targetStep = steps[targetIndex];
    if (targetStep) {
      const paragraph = targetStep.querySelector('.paragraph') || targetStep;
      paragraph.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
    }
  }
}

// Scroll to the next step
function scrollToNextStep() {
  const steps = document.querySelectorAll('.story-section__content .field__item.story-step');
  const photoArticle = document.querySelector('.photo-article');
  
  if (!steps.length || !photoArticle) return;

  // Determine target index
  // If we haven't entered stories yet (on hero), go to first step (index 0)
  // Otherwise go to next step
  let targetIndex = hasEnteredStories ? currentStepIndex + 1 : 0;
  
  // If we're at the last step, scroll to additional content
  if (targetIndex >= steps.length) {
    const additionalContent = document.querySelector('.photo-article__additional-content');
    if (additionalContent) {
      additionalContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return;
  }

  // Scroll to the target step using scrollIntoView
  const targetStep = steps[targetIndex];
  if (targetStep) {
    const paragraph = targetStep.querySelector('.paragraph') || targetStep;
    paragraph.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center',
      inline: 'nearest'
    });
  }
}

// Update arrow visibility based on current step
function updateScrollArrows() {
  const steps = document.querySelectorAll('.story-section__content .field__item.story-step');
  const additionalContent = document.querySelector('.photo-article__additional-content');

  // Back arrow - disable if on hero (not entered stories yet)
  if (scrollArrowBack) {
    if (!hasEnteredStories || currentStepIndex < 0) {
      scrollArrowBack.classList.add('is-disabled');
      scrollArrowBack.setAttribute('disabled', 'disabled');
    } else {
      scrollArrowBack.classList.remove('is-disabled');
      scrollArrowBack.removeAttribute('disabled');
    }
  }

  // Next arrow - hide if at last step and additional content is in view
  if (scrollArrowNext) {
    if (hasEnteredStories && currentStepIndex >= steps.length - 1) {
      if (additionalContent) {
        const rect = additionalContent.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.5) {
          scrollArrowNext.classList.add('is-hidden');
          return;
        }
      }
    }
    scrollArrowNext.classList.remove('is-hidden');
  }
}

// sticky step enter
function stickyStepEnter(response) {
  // Mark that we've entered the stories section
  hasEnteredStories = true;
  currentStepIndex = response.index;

  stickyStep.classed('is-active', function (d, i) {
    return i === response.index;
  });

  updateScrollArrows();
}

function init() {
  // Video handling
  findAndMuteVideos();
  setTimeout(findAndMuteVideos, 100);
  setTimeout(findAndMuteVideos, 500);

  const photoArticle = document.querySelector('.photo-article');
  if (photoArticle) {
    const videoObserver = new MutationObserver(findAndMuteVideos);
    videoObserver.observe(photoArticle, { childList: true, subtree: true });
  }

  // Check if we have steps
  if (!stickyStep.size()) return;

  // Store total steps
  totalSteps = stickyStep.size();

  // Create scroll navigation arrows
  createScrollArrows();

  // Initial resize
  handleResize();

  // Setup scrollama
  stickyScroller
    .setup({
      step: '.story-section__content .field__item.story-step',
      offset: 0.5,
    })
    .onStepEnter(stickyStepEnter);

  // Listen for scroll to update arrow visibility (reuse photoArticle from above)
  if (photoArticle) {
    photoArticle.addEventListener('scroll', updateScrollArrows, { passive: true });
  }

  // Keyboard navigation with arrow keys
  document.addEventListener('keydown', handleKeyboardNavigation);

  window.addEventListener('resize', handleResize);
}

// Handle keyboard arrow key navigation
function handleKeyboardNavigation(event) {
  // Only handle if we're on the photo-article page
  const photoArticle = document.querySelector('.photo-article');
  if (!photoArticle) return;

  // Don't interfere if user is typing in an input/textarea
  const activeElement = document.activeElement;
  if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable)) {
    return;
  }

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      scrollToNextStep();
      break;
    case 'ArrowUp':
      event.preventDefault();
      scrollToPrevStep();
      break;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
